import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import {
  useFeaturedItems, useSaveFeatured, useDeleteFeatured,
  useTvFeaturedShow, useTvFeaturedAuto, useLoyaltySettings,
} from '../../queries/loyalty';
import { useCafeMedia } from '../../queries/cafeWizard';
import MediaPicker from '../Cafe/wizard/MediaPicker';
import LoyaltyNav from './LoyaltyNav';
import '../../styles/honey-ink.css';
import { useInHub } from '../../nav/useInHub';

const EMPTY = {
  name: '', name_ar: '', description: '', description_ar: '',
  price: 0, discount_price: null, image_media_id: null, active: true,
};

/**
 * F-13/F-14/F-17 follow-on: character guidance for the Featured Item TV
 * card, EMPIRICAL not computed — every number below is read off a real
 * screen, not derived from a formula (a font-glyph-width-only version of
 * this table was wrong twice: once for the overall budget, once again for
 * assuming English was always the large "primary" line — see
 * featured_item_overflow_fix memory).
 *
 *   `comfortable` — verified to render in full, no ellipsis, in the worst
 *   realistic case tested.
 *
 *   `risk` — at or past the point real testing found the first ellipsis.
 *
 * These are guidance, not a hard stop — the template's shrink pass is
 * still the safety net for text written before this existed, so
 * `maxLength` is deliberately NOT set on these fields.
 *
 * F-17: the NAME no longer has one fixed shape. Which language renders
 * large-on-top ("primary") vs small-below ("secondary") now follows the
 * venue's own contentLanguage setting, exactly like the description
 * already did — never hardcoded to English. Primary gets 2 lines and
 * shrinks toward a floor before ever truncating (generous, high
 * headroom); secondary is a single line with NO further shrink lever
 * once it hits its floor, so it hits its own ellipsis far sooner — real
 * hardware sweeps of both roles, both languages, both directions, found
 * genuinely different capacities (down to a ~35-char English string
 * truncating while a DIFFERENT ~35-char string didn't — word shape, not
 * just character count, decides real wrapped width. Budgeted
 * conservatively for that variance, not the best case seen).
 */
const NAME_BUDGET = {
  primary: {
    en: { comfortable: 45 },
    ar: { comfortable: 40 },
  },
  secondary: {
    en: { comfortable: 20, risk: 30 },
    ar: { comfortable: 30, risk: 38 },
  },
};

const TEXT_BUDGET = {
  description: { comfortable: 120, risk: 145 },
  description_ar: { comfortable: 150, risk: 180 },
};

export default function LoyaltyFeatured() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const { data, isLoading } = useFeaturedItems();
  const save = useSaveFeatured();
  const del = useDeleteFeatured();
  const tvShow = useTvFeaturedShow();
  const tvAuto = useTvFeaturedAuto();
  const { data: media } = useCafeMedia('visual');
  const { data: loyaltySettings } = useLoyaltySettings();

  const [dialog, setDialog] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Which card's button is spinning — the mutation is shared across every card,
  // so without this every button would show a spinner at once.
  const [busyId, setBusyId] = useState(null);

  // F-17: which language the NAME renders large/primary vs small/secondary
  // follows the same setting the description uses — default 'ar' matches
  // the Android template's own default when the setting is absent.
  const primaryLang = loyaltySettings?.content_language === 'en' ? 'en' : 'ar';
  const nameBudget = primaryLang === 'en' ? NAME_BUDGET.primary.en : NAME_BUDGET.secondary.en;
  const nameArBudget = primaryLang === 'ar' ? NAME_BUDGET.primary.ar : NAME_BUDGET.secondary.ar;

  const pinned = (data || []).find((i) => i.is_pinned);
  // is_showing now means "in the live cycle", so with no pin this is how many
  // items the screens are actually rotating through.
  const rotatingCount = (data || []).filter((i) => i.is_showing).length;

  const showOnTv = (item) => {
    setBusyId(item.id);
    tvShow.mutate(item.id, { onSettled: () => setBusyId(null) });
  };

  const backToAuto = () => {
    setBusyId('auto');
    tvAuto.mutate(undefined, { onSettled: () => setBusyId(null) });
  };

  const mediaById = useMemo(() => {
    const m = {};
    (media || []).forEach((x) => { m[x.id] = x; });
    return m;
  }, [media]);

  const dialogImageUrl = dialog?.image_media_id
    ? (mediaById[dialog.image_media_id]?.thumb || mediaById[dialog.image_media_id]?.path || dialog.image_url)
    : dialog?.image_url;

  const setD = (k, v) => setDialog((d) => ({ ...d, [k]: v }));
  const valid = dialog && dialog.name?.trim() && Number(dialog.price) >= 0;

  const confirmDelete = (item) =>
    confirmDialog({
      message: t('loyalty.delete_item_confirm', { name: item.name }),
      header: t('loyalty.delete'),
      icon: 'pi pi-exclamation-triangle',
      className: 'lty-app',
      acceptClassName: 'p-button-danger',
      accept: () => del.mutate(item.id),
    });

  const submit = () => save.mutate(dialog, { onSuccess: () => setDialog(null) });

  return (
    <div className="lty-app lty-page p-4 md:p-6">
      <ConfirmDialog />
      <LoyaltyNav />
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        {!inHub ? (
        <div className="flex items-start gap-3">
          <div className="lty-head-icon"><i className="pi pi-star" /></div>
          <div>
            <div className="lty-title">{t('loyalty.featured_title')}</div>
            <div className="lty-sub">{t('loyalty.featured_sub')}</div>
          </div>
        </div>
        ) : <span />}
        <Button label={t('loyalty.add_item')} icon="pi pi-plus" onClick={() => setDialog({ ...EMPTY })} />
      </div>

      <div className="lty-onboard"><span className="ic">💡</span><span className="tx">{t('loyalty.onboard_featured')}</span></div>

      {/* A pin PAUSES the rotation on one item until the owner releases it, so
          say so plainly and keep the way back one click away. With no pin, the
          screens are cycling — worth stating outright, because "several items
          are tagged In rotation" is otherwise left to be inferred. */}
      {pinned ? (
        <div className="lty-onboard flex items-center justify-between gap-3 flex-wrap">
          <span className="tx">
            <span className="ic">📌</span>
            {t('loyalty.tv.pinned_banner', { name: pinned.name })}
          </span>
          <Button label={t('loyalty.tv.back_to_auto')} icon="pi pi-refresh" size="small" outlined
            loading={busyId === 'auto'} onClick={backToAuto} />
        </div>
      ) : rotatingCount > 1 && (
        <div className="lty-onboard">
          <span className="ic">🔄</span>
          <span className="tx">{t('loyalty.tv.rotation_banner', { count: rotatingCount })}</span>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} height="14rem" />)}
        </div>
      ) : !data?.length ? (
        <div className="opacity-60 text-center py-16">{t('loyalty.no_items')}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((item) => (
            <div key={item.id} className="lty-feat-card">
              <div className="lty-feat-img"
                style={{ backgroundImage: item.image_url ? `url("${item.image_url}")` : 'none' }} />
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="lty-feat-name">{item.name}</div>
                  <div className="flex items-center gap-1 flex-none">
                    {item.is_showing && (
                      <Tag severity="success" icon={item.is_pinned ? 'pi pi-lock' : 'pi pi-desktop'}
                        value={item.is_pinned ? t('loyalty.tv.tag_pinned') : t('loyalty.tv.tag_on_screen')} />
                    )}
                    {!item.active && <Tag value={t('loyalty.hidden')} />}
                  </div>
                </div>
                {item.name_ar && <div className="text-sm lty-sub" dir="rtl">{item.name_ar}</div>}
                <div className="mt-2 lty-price">
                  {item.discount_price != null ? (
                    <>{item.discount_price}<s>{item.price}</s></>
                  ) : item.price}
                </div>
                <div className="flex items-center justify-between gap-2 mt-3">
                  {/* Hidden items are never pushed by the scheduler, so offering
                      to force one onto the screens would be a broken promise. */}
                  <Button
                    label={item.is_pinned ? t('loyalty.tv.showing_now') : t('loyalty.tv.show_this')}
                    icon={item.is_pinned ? 'pi pi-check' : 'pi pi-desktop'}
                    size="small" outlined={!item.is_pinned}
                    disabled={!item.active || item.is_pinned}
                    loading={busyId === item.id}
                    onClick={() => showOnTv(item)}
                  />
                  <div className="flex gap-1">
                    <Button icon="pi pi-pencil" rounded text onClick={() => setDialog({ ...item })} aria-label="edit" />
                    <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => confirmDelete(item)} aria-label="delete" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* add / edit */}
      <Dialog visible={!!dialog} onHide={() => setDialog(null)} style={{ width: '34rem', maxWidth: '95vw' }}
        className="lty-app"
        header={dialog?.id ? t('loyalty.edit_item') : t('loyalty.add_item')} modal
        footer={
          <div className="flex justify-end gap-2">
            <Button label={t('loyalty.cancel')} text onClick={() => setDialog(null)} />
            <Button label={t('loyalty.save')} icon="pi pi-check" disabled={!valid} loading={save.isLoading} onClick={submit} />
          </div>
        }>
        {dialog && (
          <div className="flex flex-col gap-4 pt-2">
            {/* image */}
            <div>
              <label className="block font-medium mb-1">{t('loyalty.image')}</label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-24 rounded-lg bg-center bg-cover bg-black/5 border border-black/10 flex-none"
                  style={{ backgroundImage: dialogImageUrl ? `url("${dialogImageUrl}")` : 'none' }} />
                <div className="flex flex-col gap-2">
                  <Button label={t('loyalty.choose_image')} icon="pi pi-images" outlined size="small" onClick={() => setPickerOpen(true)} />
                  {dialog.image_media_id && (
                    <Button label={t('loyalty.remove_image')} icon="pi pi-times" text size="small"
                      onClick={() => setD('image_media_id', null)} />
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Row label={t('loyalty.name_en')}
                hint={primaryLang === 'en' ? t('loyalty.name_role_primary') : t('loyalty.name_role_secondary')}>
                <InputText value={dialog.name} onChange={(e) => setD('name', e.target.value)} className="w-full" />
                <CharCounter value={dialog.name} budget={nameBudget} />
              </Row>
              <Row label={t('loyalty.name_ar')}
                hint={primaryLang === 'ar' ? t('loyalty.name_role_primary') : t('loyalty.name_role_secondary')}>
                <InputText value={dialog.name_ar || ''} onChange={(e) => setD('name_ar', e.target.value)} className="w-full" dir="rtl" />
                <CharCounter value={dialog.name_ar} budget={nameArBudget} />
              </Row>
              <Row label={t('loyalty.price')}>
                <InputNumber value={dialog.price} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                  className="w-full" onValueChange={(e) => setD('price', e.value ?? 0)} />
              </Row>
              <Row label={t('loyalty.discount_price')} hint={t('loyalty.discount_price_hint')}>
                <InputNumber value={dialog.discount_price} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                  className="w-full" onValueChange={(e) => setD('discount_price', e.value ?? null)} />
              </Row>
            </div>
            <Row label={t('loyalty.description_en')}>
              <InputTextarea value={dialog.description || ''} onChange={(e) => setD('description', e.target.value)} rows={2} className="w-full" autoResize />
              <CharCounter value={dialog.description} budget={TEXT_BUDGET.description} />
            </Row>
            <Row label={t('loyalty.description_ar')}>
              <InputTextarea value={dialog.description_ar || ''} onChange={(e) => setD('description_ar', e.target.value)} rows={2} className="w-full" dir="rtl" autoResize />
              <CharCounter value={dialog.description_ar} budget={TEXT_BUDGET.description_ar} />
            </Row>
            <div className="flex items-center gap-2">
              <InputSwitch checked={!!dialog.active} onChange={(e) => setD('active', e.value)} />
              <span>{t('loyalty.live_on_tv')}</span>
            </div>
          </div>
        )}
      </Dialog>

      {/* media picker */}
      <Dialog visible={pickerOpen} onHide={() => setPickerOpen(false)} style={{ width: '52rem', maxWidth: '96vw' }}
        className="lty-app" header={t('loyalty.choose_image')} modal>
        <MediaPicker
          family="visual"
          value={dialog?.image_media_id ? [dialog.image_media_id] : []}
          onChange={(ids) => {
            const last = ids && ids.length ? ids[ids.length - 1] : null;
            setD('image_media_id', last);
            setPickerOpen(false);
          }}
        />
      </Dialog>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div>
      <label className="block font-medium mb-1">{label}</label>
      {hint && <div className="text-sm opacity-60 mb-1">{hint}</div>}
      {children}
    </div>
  );
}

/**
 * Live guidance while typing — see TEXT_BUDGET's own docblock for where the
 * numbers come from. Three states, not a single cutoff:
 *   - under `comfortable`: neutral — fits at full size, nothing to say
 *   - over `comfortable`: amber — still shows (the template's shrink pass
 *     handles it), just smaller than it would otherwise be
 *   - over `risk` (description fields only): red — long enough that the
 *     description itself may be dropped rather than merely shrunk
 * Never blocks typing — this is guidance, not a hard limit.
 */
function CharCounter({ value, budget }) {
  const { t } = useTranslation();
  const n = (value || '').length;
  const tone = budget.risk && n > budget.risk ? 'risk' : n > budget.comfortable ? 'shrink' : 'ok';
  const color = tone === 'risk' ? '#c0392b' : tone === 'shrink' ? '#b8860b' : 'inherit';
  const message = tone === 'risk'
    ? t('loyalty.chars_risk')
    : tone === 'shrink'
      ? t('loyalty.chars_shrink')
      : null;

  return (
    <div className="text-xs mt-1 flex items-center gap-2" style={{ opacity: tone === 'ok' ? 0.5 : 1 }}>
      <span style={{ color }}>{n}/{budget.comfortable}</span>
      {message && <span style={{ color }}>{message}</span>}
    </div>
  );
}
